/**
 * Client-side mock “AI” for inbound (client) email. Replace with a real model later.
 */

/** Shown in admin UI while analysing and on completed reads (swap when wiring a real API). */
export const MOCK_CLIENT_REPLY_ANALYSIS_MODEL = "Gemini 2.0 Flash";

function clip(s, max) {
  const t = (s || "").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/**
 * Mock analysis of a **client** (inbound) message — sentiment, intent, risks, suggested reply angles.
 * @param {{ subject: string, body: string, from?: string }} input
 * @returns {Promise<{
 *   verdict: 'favorable' | 'mixed' | 'needs_attention',
 *   summary: string,
 *   sentimentLabel: string,
 *   clientIntent: string,
 *   confidence: number,
 *   concerns: string[],
 *   replyAngles: string[],
 *   offerSignal: 'accept_offer' | 'reject_offer' | 'negotiate' | 'unclear',
 *   offerLabel: string,
 *   analyzedAt: string,
 *   modelLabel: string
 * }>}
 */
export function runMockClientReplyAnalysis({ subject, body, from, forceFail, failMessage }) {
  const sub = (subject || "").trim();
  const txt = (body || "").trim();
  const failText =
    (typeof failMessage === "string" && failMessage.trim()) ||
    "The model could not finish this read (mock: simulated failure). Try Re-analyse.";
  return new Promise((resolve, reject) => {
    window.setTimeout(() => {
      if (forceFail) {
        reject(new Error(failText));
        return;
      }
      resolve({
        ...computeClientInboundAnalysis(sub, txt, from),
        modelLabel: MOCK_CLIENT_REPLY_ANALYSIS_MODEL,
      });
    }, 450);
  });
}

function computeClientInboundAnalysis(subject, body, from) {
  const combined = `${subject} ${body}`.toLowerCase();
  const concerns = [];

  if (/\b(angry|disappointed|unacceptable|refund|lawyer|legal|complaint)\b/i.test(combined)) {
    concerns.push("Strong or legal-tinged language — prioritize a careful, documented response.");
  }
  if (/\b(asap|immediately|today|now)\b/i.test(combined) && body.length > 40) {
    concerns.push("Time pressure signaled — set realistic expectations in your reply.");
  }
  const qCount = (body.match(/\?/g) || []).length;
  if (qCount >= 3) {
    concerns.push("Several questions bundled — consider numbering your answers to match.");
  }
  if (/\b(does not include|not included|extra cost|change order)\b/i.test(combined)) {
    concerns.push("Scope / pricing sensitivity — align with contract or SOW before committing.");
  }

  const positive =
    /\b(thanks|thank you|great|perfect|sounds good|approved|works for us|aligned|yes)\b/i.test(combined);
  const negative = concerns.length >= 2 || /\b(unhappy|concerned|worried|issue|problem)\b/i.test(combined);

  let verdict = "mixed";
  if (negative) verdict = "needs_attention";
  else if (positive && concerns.length === 0) verdict = "favorable";

  const sentimentLabel =
    verdict === "favorable" ? "Positive / cooperative" : verdict === "needs_attention" ? "Sensitive" : "Neutral";

  const clientIntent = /\b\?/.test(body)
    ? "Seeking clarification or decision"
    : positive
      ? "Acknowledging or accepting prior proposal"
      : "Continuing thread / status update";

  const summary =
    verdict === "favorable"
      ? "The client’s tone looks constructive. They appear aligned; you can reinforce next steps and owners."
      : verdict === "needs_attention"
        ? "This reply may need a measured response: address concerns explicitly and avoid over-promising."
        : "Mixed signals — confirm understanding, answer concrete questions, and propose a short call if needed.";

  const replyAngles = [
    verdict === "favorable"
      ? "Thank them, restate agreed next steps with dates, and offer a single point of contact."
      : "Open with acknowledgment, answer each thread item, and separate facts from decisions still pending.",
    concerns.length
      ? `Directly address: ${clip(concerns[0], 72)}`
      : "Close with one clear ask (e.g. approval to proceed or a time for a sync).",
  ];

  const confidence =
    verdict === "favorable" ? 0.84 : verdict === "needs_attention" ? 0.68 : 0.72;

  const { offerSignal, offerLabel } = inferOfferSignal(combined, body);

  const professionalSuggestion = buildMockProfessionalSuggestion(combined, concerns, verdict);
  const budgetAssessment = buildMockBudgetAssessment(combined);

  return {
    verdict,
    summary,
    sentimentLabel,
    clientIntent,
    confidence,
    concerns,
    replyAngles,
    offerSignal,
    offerLabel,
    analyzedAt: new Date().toISOString(),
    analysisSchemaVersion: 2,
    professionalSuggestion,
    budgetAssessment,
  };
}

/**
 * Mock extended analysis — mirrors `professionalSuggestion` in API docs.
 */
function buildMockProfessionalSuggestion(combined, concerns, verdict) {
  const heavy =
    /\b(security|architecture|legacy|erp|kubernetes|integrat|compliance|penetration)\b/i.test(combined);
  const level = heavy ? "senior" : /\b(mvp|simple|landing|small)\b/i.test(combined) ? "mid" : "mid";
  return {
    recommendedService: heavy
      ? "Senior-led discovery + phased implementation"
      : "Structured discovery and milestone-based delivery",
    whyThisService:
      verdict === "needs_attention"
        ? "The thread signals constraints; align delivery with experienced oversight."
        : "Fits the scope implied by the message and typical request patterns.",
    requiredProfessionalLevel: level,
    requiredSkills: [
      heavy ? "integration / risk-aware delivery" : "requirements & delivery",
      "stakeholder communication",
    ],
    deliveryApproach: "Short discovery → agreed MVP → measured iteration",
    riskIfUnderScoped:
      concerns.length > 0 ? [concerns[0]] : ["Misaligned expectations if scope is not confirmed in writing"],
    suggestedNextQuestion: "What is your target date and who signs off on scope changes?",
  };
}

/**
 * Mock extended analysis — mirrors `budgetAssessment` in API docs.
 */
function buildMockBudgetAssessment(combined) {
  let status = "unclear";
  let fitScore = 58;
  let clientBudgetMentioned = null;
  if (/\b(15\s*k|15000|€\s*15|15,000)\b/i.test(combined)) {
    status = "acceptable";
    fitScore = 72;
    clientBudgetMentioned = "~15k (inferred from text)";
  } else if (/\b(500|1000|under\s*1\s*k)\b/i.test(combined) && /\b(budget|only|afford)\b/i.test(combined)) {
    status = "low";
    fitScore = 34;
    clientBudgetMentioned = "tight budget (inferred)";
  } else if (/\b(50\s*k|100\s*k|50000|100000)\b/i.test(combined)) {
    status = "good";
    fitScore = 86;
    clientBudgetMentioned = "strong range (inferred)";
  }
  return {
    status,
    fitScore,
    estimatedRangeMin: 12000,
    estimatedRangeMax: 24000,
    currency: "USD",
    clientBudgetMentioned,
    gapNote:
      status === "low"
        ? "Likely below a full-scope build; consider a slimmer MVP or phased delivery."
        : status === "unclear"
          ? "No explicit budget in the email — confirm range on the next touchpoint."
          : "Rough internal band for typical scope — validate before committing.",
    negotiationAdvice: [
      "Anchor on written scope and deliverables.",
      "Offer phased milestones if timeline or budget is constrained.",
    ],
    suggestedClientReply:
      status === "unclear"
        ? "Thanks — could you share your budget range or constraints so we can propose a realistic scope and timeline?"
        : "Thanks for the context. We can align the plan with your budget; happy to walk through a phased approach on a short call.",
  };
}

/**
 * Classify how the client is responding to pricing / proposal / offer language.
 * @returns {{ offerSignal: 'accept_offer'|'reject_offer'|'negotiate'|'unclear', offerLabel: string }}
 */
function inferOfferSignal(combined, body) {
  const reject =
    /\b(declin(e|ing)|reject(ed|ing)?|not interested|pass on this|going with (another|a different)|won't proceed|will not proceed|too expensive|out of budget|can't afford|cannot accept|no thank|pull out|withdraw)\b/i.test(
      combined
    );
  const accept =
    /\b(accept(ed|ance)?|approve(d)?|sign off|go ahead|green light|proceed|confirmed|works for us|we('re| are) aligned|happy to move forward|deal|agreed on)\b/i.test(
      combined
    );
  const negotiate =
    /\b(negotiate|negotiation|counter(-offer)?|discount|lower (the )?price|meet (us )?halfway|revise (the )?(quote|proposal|pricing)|ballpark|if you can|better terms|cap on|flexibility on)\b/i.test(
      combined
    );

  if (reject && !accept) {
    return { offerSignal: "reject_offer", offerLabel: "Client rejected our offer" };
  }
  if (accept && !reject && !negotiate) {
    return { offerSignal: "accept_offer", offerLabel: "Client accepted our offer" };
  }
  if (negotiate && !accept) {
    return { offerSignal: "negotiate", offerLabel: "Client wants to negotiate" };
  }
  if (accept && negotiate) {
    return { offerSignal: "negotiate", offerLabel: "Client wants to negotiate" };
  }
  if (accept && reject) {
    return { offerSignal: "unclear", offerLabel: "Offer stance unclear" };
  }
  if (/\b(quote|proposal|pricing|scope|phase|sow|contract)\b/i.test(combined) && body.length > 80) {
    return { offerSignal: "negotiate", offerLabel: "Commercial / scope discussion" };
  }
  return { offerSignal: "unclear", offerLabel: "Offer stance unclear" };
}

/**
 * @param {{
 *   clientEmail: string,
 *   subject: string,
 *   body: string,
 *   fromAddress?: string
 * }} p
 */
export function buildMockOutboundEmail({
  clientEmail,
  subject,
  body,
  fromAddress = "team@vizhacks.example",
}) {
  return {
    id: `mock-out-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    direction: "outbound",
    createdAt: new Date().toISOString(),
    subject: (subject || "").trim() || "(no subject)",
    body,
    from: fromAddress,
    to: (clientEmail || "").trim(),
  };
}

const INBOUND_VARIANTS = {
  primary: (subj) => ({
    body:
      "Thanks for the update — this works on our side.\n\n" +
      "We’re aligned with the next steps you outlined. Could you confirm whether the analytics dashboards are in scope for the fixed phase, or billed separately?\n\n" +
      "Best regards",
    subjectLine: subj ? `Re: ${subj}` : "Re: your message",
  }),
  short: (subj) => ({
    body:
      "Got it, thanks. Let’s proceed as discussed — please send the calendar invite for the review.\n\nThanks again.",
    subjectLine: subj ? `Re: ${subj}` : "Re: your message",
  }),
};

/**
 * @param {{ clientEmail: string, subject: string, teamAddress?: string, variant?: 'primary' | 'short' }} p
 */
export function buildMockInboundReply({
  clientEmail,
  subject,
  teamAddress = "team@vizhacks.example",
  variant = "primary",
}) {
  const subj = (subject || "").trim();
  const pack = (INBOUND_VARIANTS[variant] || INBOUND_VARIANTS.primary)(subj);
  return {
    id: `mock-in-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    direction: "inbound",
    createdAt: new Date().toISOString(),
    subject: pack.subjectLine,
    body: pack.body,
    from: (clientEmail || "").trim() || "client@example.com",
    to: teamAddress,
  };
}

/**
 * Second beat in a mock thread (e.g. quick “thanks” after your team answers).
 * @param {{ clientEmail: string, subject: string, teamAddress?: string }} p
 */
export function buildMockInboundFollowUp({ clientEmail, subject, teamAddress = "team@vizhacks.example" }) {
  const subj = (subject || "").trim();
  return {
    id: `mock-in-fu-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    direction: "inbound",
    createdAt: new Date().toISOString(),
    subject: subj ? `Re: ${subj}` : "Re: your message",
    body:
      "Perfect — that answers our question. We’re good to move forward on this basis.\n\nAppreciate the quick turnaround.",
    from: (clientEmail || "").trim() || "client@example.com",
    to: teamAddress,
  };
}

/**
 * Pre-built mock thread for demos (local overlay — not persisted to API).
 * @param {{ clientEmail: string, teamAddress?: string, threadTopic?: string }} p
 */
export function buildMockConversationSeed({
  clientEmail,
  teamAddress = "team@vizhacks.example",
  threadTopic = "Scope & delivery",
}) {
  const client = (clientEmail || "").trim() || "client@example.com";
  const now = Date.now();
  const day = 86400000;
  return [
    {
      id: "mock-seed-in-1",
      direction: "inbound",
      createdAt: new Date(now - day * 3).toISOString(),
      subject: threadTopic,
      body:
        "Hi team,\n\n" +
        "We’d like to lock phase 2 scope before Friday. Can you confirm what’s included in the fixed package vs optional add-ons?\n\nThanks,\nClient team",
      from: client,
      to: teamAddress,
    },
    {
      id: "mock-seed-out-1",
      direction: "outbound",
      createdAt: new Date(now - day * 3 + 2 * 3600000).toISOString(),
      subject: `Re: ${threadTopic}`,
      body:
        "Thanks for the note — happy to clarify.\n\n" +
        "Fixed phase covers core integration and UAT support. Analytics dashboard mockups are scoped as a separate line item; we can price that today if you want it in phase 2.\n\nLet me know if you want a short call to align.",
      from: teamAddress,
      to: client,
    },
    {
      id: "mock-seed-in-2",
      direction: "inbound",
      createdAt: new Date(now - day * 2).toISOString(),
      subject: `Re: ${threadTopic}`,
      body:
        "Understood on the split. Please add the dashboard mockups to phase 2 with a cap on revisions (2 rounds). We can sign off by EOD tomorrow if numbers match the ballpark you shared.\n\nRegards",
      from: client,
      to: teamAddress,
    },
    {
      id: "mock-seed-in-3",
      direction: "inbound",
      createdAt: new Date(now - 45 * 60 * 1000).toISOString(),
      subject: `Re: ${threadTopic}`,
      body:
        "One more thing before we countersign — can you confirm the SLA window for P1 incidents (business hours only vs 24/7)? That’s the last open item on our side.\n\nThanks,\nClient team",
      from: client,
      to: teamAddress,
      /** Keeps the AI strip on “Analysing…” long enough to see in demos (ms). */
      mockAnalysisDelayMs: 4200,
    },
    {
      id: "mock-seed-in-fail",
      direction: "inbound",
      createdAt: new Date(now - 20 * 60 * 1000).toISOString(),
      subject: `Re: ${threadTopic}`,
      body:
        "Looping in legal for a quick review on indemnities — we’ll send the redlines tomorrow unless you need them tonight.\n\nThanks",
      from: client,
      to: teamAddress,
      mockAnalysisDelayMs: 1400,
      mockAnalysisFail: true,
      mockAnalysisErrorMessage:
        "Model stopped generating (mock). Safety filter or timeout — retry shortly or shorten the thread context.",
    },
  ];
}
